import { Domain } from '@superfunnel/types';
import { useMemo } from 'react';
import ConfiguredSectionPlaceholder from './ConfiguredSectionPlaceholder';

interface Props {
  domain?: Domain;
}

const ConfiguredSection = ({ domain }: Props) => {
  const isSubdomain = useMemo(() => {
    if (!domain?.domain || !domain?.metadata?.vercel?.apexName) return false;
    return domain.domain !== domain.metadata.vercel.apexName;
  }, [domain]);

  const subdomainHandle = useMemo(() => {
    if (!isSubdomain || !domain?.domain || !domain?.metadata?.vercel?.apexName)
      return null;
    const apexName = domain.metadata.vercel.apexName;
    const fullDomain = domain.domain;
    return fullDomain.replace(`.${apexName}`, '');
  }, [isSubdomain, domain]);

  const recordType = useMemo(
    () => (isSubdomain ? 'CNAME' : 'A'),
    [isSubdomain],
  );

  const recommendedIP = useMemo(() => {
    if (isSubdomain) return null;
    const ips = domain?.metadata?.vercel?.config?.recommendedIPv4?.[0]?.value;
    return ips?.[0] ?? '76.76.21.21';
  }, [isSubdomain, domain]);

  const recommendedCNAME = useMemo(() => {
    if (!isSubdomain) return null;
    return (
      domain?.metadata?.vercel?.config?.recommendedCNAME?.[0]?.value ??
      'cname.vercel-dns.com.'
    );
  }, [isSubdomain, domain]);

  const txtVerification = useMemo(() => {
    const verified = domain?.metadata?.vercel?.verified;
    const verification = domain?.metadata?.vercel?.verification;
    if (verified !== false || !verification || verification.length === 0) {
      return null;
    }
    return verification.find((v: any) => v.type === 'TXT') ?? null;
  }, [domain]);

  const txtRecordName = useMemo(() => {
    if (!txtVerification || !domain?.metadata?.vercel?.apexName) return '';
    const fullDomain = txtVerification.domain;
    const apexName = domain.metadata.vercel.apexName;
    return fullDomain.replace(`.${apexName}`, '');
  }, [txtVerification, domain]);

  if (!domain) {
    return <ConfiguredSectionPlaceholder />;
  }

  return (
    <>
      {domain.status !== 'approved' && (
        <>
          <div className="mt-5 mb-8 w-full border-t border-gray-100" />

          <div className="font-inter text-left">
            <p className="mb-2 text-sm font-semibold text-gray-900">
              DNS Records
            </p>
            <p className="mb-3 text-sm text-gray-600">
              Set the following DNS records at your provider:
            </p>

            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {recordType === 'CNAME' ? (
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        CNAME
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        {subdomainHandle ?? 'www'}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        {recommendedCNAME}
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        A
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        @
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        {recommendedIP}
                      </td>
                    </tr>
                  )}
                  {txtVerification && (
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        {txtVerification.type}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-900">
                        {txtRecordName}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm break-all text-gray-900">
                        {txtVerification.value}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ConfiguredSection;
